import React, { useState, useEffect } from 'react';
import { Typography, Grid, Paper, Box } from '@mui/material';
import ButtonWithCheckbox from './components/buttonWithCheckbox';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

export default function CycleHistoryAndStatus() {
  const navigate = useNavigate();
  const patientData = useSelector((state) => state.clinicalReducer);
  const intermediateData = patientData.currentCycle;
  const allCycles = patientData.patientCycles || [];
  
  const [selectedCycle, setSelectedCycle] = useState(null);

  // Initialize with current active cycle by default
  useEffect(() => {
    if (intermediateData && intermediateData.id) {
      setSelectedCycle({ data: intermediateData, isActive: true, type: 'current' });
    } else if (allCycles.length > 0) {

      setSelectedCycle({ data: allCycles[0], isActive: allCycles[0].isActive, type: 'inactive', index: 0 });
    }
  }, [intermediateData, allCycles]);

  if (
    (!intermediateData || !intermediateData.id) &&
    allCycles.length === 0
  ) {
    return null;
  }

  const buttonHandler = (activeTab, subTab) => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    const isReadOnly = selectedCycle && !selectedCycle.isActive;
    const cycleId = selectedCycle?.data?.cycleId;
    navigate(
      `/patient/${patientData.selectedPatientData.patient.id}/clinical-management?tab=${activeTab}&subTab=${subTab}&cycleId=${cycleId}&readOnly=${isReadOnly}`,
    );
  };

  const handleCycleClick = (cycle, index) => {
    setSelectedCycle({ data: cycle, isActive: cycle.isActive, type: 'inactive', index });
  };

  const handleCurrentCycleClick = () => {
    setSelectedCycle({ data: intermediateData, isActive: true, type: 'current' });
  };

  const renderCycleButtons = () => {
    if (!selectedCycle) return null;

    const cycleData = selectedCycle.data;
    const isActive = selectedCycle.isActive;
    
    const isFmsChecked = true;
    const isEtChecked = cycleData?.fetSheet?.procedureDate ? true : false;
    const isOpuChecked = cycleData?.fmsData?.procedureDate ? true : false;

    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        gap={2}
      >
        <ButtonWithCheckbox
          displayKey={'FMS'}
          checked={isFmsChecked}
          lastUpdated={cycleData?.fms?.updatedAt}
          by={cycleData?.referral?.referringPhysician}
          onClickHandler={() => buttonHandler(5, 1)}
          showCaption={false}
          isInactive={!isActive}
        />
        <ButtonWithCheckbox
          displayKey={'FET'}
          checked={isEtChecked}
          lastUpdated={cycleData?.fet?.updatedAt}
          by={cycleData?.referral?.referringPhysician}
          onClickHandler={() => buttonHandler(5, 2)}
          showCaption={false}
          isInactive={!isActive}
        />
        <ButtonWithCheckbox
          displayKey={'OPU'}
          checked={isOpuChecked}
          lastUpdated={cycleData?.opu?.updatedAt}
          by={cycleData?.referral?.referringPhysician}
          onClickHandler={() => buttonHandler(5, 3)}
          showCaption={false}
          isInactive={!isActive}
        />
      </Box>
    );
  };

  return (
    <Paper sx={{ padding: 3, marginBottom: 3, boxShadow: 3, borderRadius: 2 }}>
      <Grid container spacing={2}>
        {/* Left Side: Cycle History Table */}
        <Grid item xs={8}>
          <Box>
            <table style={{ borderCollapse: 'collapse', width: '70%' }}>
              <thead>
                <tr>
                  <th
                    style={{
                      borderBottom: '2px solid #ccc',
                      padding: '8px',
                      width: '50%',
                    }}
                  >
                    Cycle
                  </th>
                  <th
                    style={{
                      borderBottom: '2px solid #ccc',
                      padding: '8px',
                      width: '50%',
                    }}
                  >
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {intermediateData && intermediateData.id && (
                  <tr 
                    style={{ 
                      cursor: 'pointer', 
                      backgroundColor: selectedCycle?.type === 'current' ? '#e3f2fd' : 'transparent' 
                    }}
                    onClick={handleCurrentCycleClick}
                  >
                    <td
                      style={{
                        borderBottom: '1px solid #ccc',
                        padding: '8px',
                      }}
                    >
                      Cycle {allCycles.length + 1}
                    </td>
                    <td
                      style={{
                        borderBottom: '1px solid #ccc',
                        padding: '8px',
                        color: 'green',
                      }}
                    >
                      Ongoing
                    </td>
                  </tr>
                )}
               
                {allCycles.map((cycle, index) => {
                  const isSelected = selectedCycle?.type === 'inactive' && selectedCycle?.index === index;
                  return (
                    <tr 
                      key={index} 
                      style={{ 
                        cursor: 'pointer', 
                        backgroundColor: isSelected ? '#e3f2fd' : 'transparent' 
                      }}
                      onClick={() => handleCycleClick(cycle, index)}
                    >
                      <td
                        style={{
                          borderBottom: '1px solid #ccc',
                          padding: '8px',
                        }}
                      >
                        Cycle {allCycles.length - index}
                      </td>
                      <td
                        style={{
                          borderBottom: '1px solid #ccc',
                          padding: '8px',
                          color: cycle.isActive ? 'green' : 'red',
                        }}
                      >
                        {cycle.isActive ? 'Ongoing' : 'Stopped'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Grid>

        {/* Right Side: Buttons for selected cycle only */}
        <Grid item xs={4}>
          <Box
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
            height="100%"
            gap={3}
          >
            {selectedCycle && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, textAlign: 'center' }}>
                  {selectedCycle.type === 'current' 
                    ? 'Current Cycle' 
                    : `Cycle ${allCycles.length - selectedCycle.index} ${!selectedCycle.isActive ? '(Inactive)' : ''}`
                  }
                </Typography>
                {renderCycleButtons()}
              </Box>
            )}
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
}
